import { skeleton, type PixelFrame, type Pose } from "pixelfx";

/**
 * Манекен в полном разрешении арта: кости — из скелета pixelfx (те же позы), тело — объёмные сегменты в 190 px.
 * Свет сверху-слева, тень снизу-справа, тёмная обводка по силуэту и по краю ближних конечностей. Временный боец,
 * пока нет героев способа Б (ADR 0002).
 */
const RIG_TO_ART = 5;
const BOX_W = 440;
const BOX_H = 240;
const FOOT_X = 220;
const FOOT_Y = 232;

interface Segment {
  readonly from: string;
  readonly to: string;
  readonly r0: number;
  readonly r1: number;
  /** 0 — дальние конечности, 1 — корпус и голова, 2 — ближние. */
  readonly depth: 0 | 1 | 2;
  /** Конечность: между её сегментами обводки нет. */
  readonly limb: number;
}

const SEGMENTS: readonly Segment[] = [
  { from: "hipF", to: "kneeF", r0: 2.4, r1: 2.0, depth: 0, limb: 1 },
  { from: "kneeF", to: "ankleF", r0: 2.0, r1: 1.5, depth: 0, limb: 1 },
  { from: "ankleF", to: "toeF", r0: 1.4, r1: 1.1, depth: 0, limb: 1 },
  { from: "shoulderF", to: "elbowF", r0: 2.0, r1: 1.6, depth: 0, limb: 2 },
  { from: "elbowF", to: "handF", r0: 1.6, r1: 1.9, depth: 0, limb: 2 },
  { from: "shoulderF", to: "shoulderN", r0: 2.2, r1: 2.2, depth: 1, limb: 3 },
  { from: "pelvis", to: "waist", r0: 3.4, r1: 3.0, depth: 1, limb: 3 },
  { from: "waist", to: "chest", r0: 3.1, r1: 3.9, depth: 1, limb: 3 },
  { from: "chest", to: "neck", r0: 3.0, r1: 1.4, depth: 1, limb: 3 },
  { from: "headCenter", to: "headCenter", r0: 3.3, r1: 3.3, depth: 1, limb: 3 },
  { from: "hipN", to: "kneeN", r0: 2.5, r1: 2.1, depth: 2, limb: 4 },
  { from: "kneeN", to: "ankleN", r0: 2.1, r1: 1.6, depth: 2, limb: 4 },
  { from: "ankleN", to: "toeN", r0: 1.5, r1: 1.2, depth: 2, limb: 4 },
  { from: "shoulderN", to: "elbowN", r0: 2.1, r1: 1.7, depth: 2, limb: 5 },
  { from: "elbowN", to: "handN", r0: 1.7, r1: 2.0, depth: 2, limb: 5 },
];

/** Тона стороны: [тушь, тень, середина, свет]; дальние конечности — на ступень темнее. */
const SIDE_TONES: readonly (readonly number[])[] = [
  [0xff140c0a, 0xff6a4430, 0xffb48a62, 0xffefd2ae],
  [0xff0a0c16, 0xff283a6e, 0xff5068bc, 0xff9ab0f0],
];
const FAR_TONES: readonly (readonly number[])[] = [
  [0xff140c0a, 0xff503222, 0xff8a6648, 0xffb89a7a],
  [0xff0a0c16, 0xff1c2a52, 0xff3a4c8e, 0xff6e84c0],
];

const ids = new Uint8Array(BOX_W * BOX_H);

function stamp(index: number, ax: number, ay: number, bx: number, by: number, r0: number, r1: number): void {
  const left = Math.max(0, Math.floor(Math.min(ax - r0, bx - r1)));
  const right = Math.min(BOX_W - 1, Math.ceil(Math.max(ax + r0, bx + r1)));
  const top = Math.max(0, Math.floor(Math.min(ay - r0, by - r1)));
  const bottom = Math.min(BOX_H - 1, Math.ceil(Math.max(ay + r0, by + r1)));
  const dx = bx - ax;
  const dy = by - ay;
  const length = dx * dx + dy * dy || 1;
  for (let y = top; y <= bottom; y++) {
    for (let x = left; x <= right; x++) {
      const t = Math.max(0, Math.min(1, ((x + 0.5 - ax) * dx + (y + 0.5 - ay) * dy) / length));
      const px = ax + dx * t - (x + 0.5);
      const py = ay + dy * t - (y + 0.5);
      const radius = r0 + (r1 - r0) * t;
      if (px * px + py * py <= radius * radius) ids[y * BOX_W + x] = index;
    }
  }
}

export interface BodyDraw {
  readonly frame: PixelFrame;
  /** Пиксели бойцов в кадре: номер стороны + 1, для замера контраста. */
  readonly mask: Uint8Array;
  readonly pose: Pose;
  readonly facing: number;
  readonly lying: boolean;
  readonly invisible: boolean;
  /** Стопы в кадре, пиксели арта. */
  readonly x: number;
  readonly y: number;
  readonly side: number;
  readonly tick: number;
}

export function drawBody(draw: BodyDraw): void {
  const joints = skeleton(draw.pose).joints;
  const point = (name: string): [number, number] => {
    const head = joints.head ?? [0, 0, 0];
    const crown = joints.crown ?? head;
    const joint =
      name === "headCenter"
        ? ([(head[0] + crown[0]) / 2, (head[1] + crown[1]) / 2, head[2]] as const)
        : (joints[name] ?? [0, 0, 0]);
    let x = joint[0];
    let y = joint[1];
    if (draw.lying) [x, y] = [y, x - 6];
    return [FOOT_X + x * RIG_TO_ART * draw.facing, FOOT_Y + y * RIG_TO_ART];
  };
  ids.fill(0);
  SEGMENTS.forEach((segment, index) => {
    const [ax, ay] = point(segment.from);
    const [bx, by] = point(segment.to);
    stamp(index + 1, ax, ay, bx, by, segment.r0 * RIG_TO_ART, segment.r1 * RIG_TO_ART);
  });
  const tones = SIDE_TONES[draw.side] ?? SIDE_TONES[0] ?? [];
  const farTones = FAR_TONES[draw.side] ?? tones;
  const { frame, mask } = draw;
  const at = (x: number, y: number): number =>
    x < 0 || y < 0 || x >= BOX_W || y >= BOX_H ? 0 : (ids[y * BOX_W + x] ?? 0);
  for (let y = 0; y < BOX_H; y++) {
    const sy = draw.y - FOOT_Y + y;
    if (sy < 0 || sy >= frame.h) continue;
    for (let x = 0; x < BOX_W; x++) {
      const id = ids[y * BOX_W + x] ?? 0;
      if (id === 0) continue;
      const sx = draw.x - FOOT_X + x;
      if (sx < 0 || sx >= frame.w) continue;
      if (draw.invisible) {
        if ((x + y + draw.tick) % 9 === 0) frame.color[sy * frame.w + sx] = 0x60ffe0c0;
        continue;
      }
      const segment = SEGMENTS[id - 1];
      if (!segment) continue;
      const sameLimb = (other: number): boolean => other !== 0 && SEGMENTS[other - 1]?.limb === segment.limb;
      const neighbours = [at(x - 1, y), at(x + 1, y), at(x, y - 1), at(x, y + 1)];
      const edge = neighbours.some(
        (other) => other === 0 || (!sameLimb(other) && (SEGMENTS[other - 1]?.depth ?? 0) < segment.depth),
      );
      const palette = segment.depth === 0 ? farTones : tones;
      let tone = 2;
      if (!sameLimb(at(x - 3, y - 4))) tone = 3;
      else if (!sameLimb(at(x + 3, y + 4)) || !sameLimb(at(x + 2, y + 6))) tone = 1;
      frame.color[sy * frame.w + sx] = (edge ? palette[0] : palette[tone]) ?? 0xff000000;
      mask[sy * frame.w + sx] = draw.side + 1;
    }
  }
}
